import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowDown,
  faArrowUp,
  faCopy,
  faDownload,
  faPencil,
  faSyncAlt,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, Link } from 'components';
import { Row } from 'react-table';

enum MoveDirection {
  up = 'up',
  down = 'down',
}

type DefaultRow = Row<any>;
type OnReorderProps = (props: { id: string; direction: MoveDirection }) => void;

interface CustomButtonProps {
  onCustomButton: (row: DefaultRow) => void;
  row: DefaultRow;
  tableId?: string;
  text?: string;
  icon?: IconDefinition;
  iconAfter?: IconDefinition;
  className?: string;
  tooltip?: string;
}

interface EditButtonProps {
  getRowEditPath: (row: DefaultRow) => string;
  disableRowEdit?: (row: DefaultRow) => boolean;
  row: DefaultRow;
  tableId: string;
}

interface ButtonUpProps {
  onReorder: OnReorderProps;
  firstElementId?: string;
  row: DefaultRow;
  tableId: string;
}

interface RemoveButtonProps {
  onRowRemove: (row: DefaultRow) => void;
  disableRowRemove?: (row: DefaultRow) => boolean;
  hideRowRemove?: (row: DefaultRow) => boolean;
  row: DefaultRow;
  tableId: string;
}

interface DownloadButtonProps {
  onRowDownload: (row: DefaultRow) => void;
  row: DefaultRow;
  tableId: string;
}

interface DuplicateButtonProps {
  onRowDuplicate: (row: DefaultRow) => void;
  row: DefaultRow;
  tableId: string;
}

interface GetLatestButtonProps {
  onRowGetLatest: (row: DefaultRow) => void;
  row: DefaultRow;
  tableId: string;
}

interface ButtonDownProps {
  onReorder: OnReorderProps;
  lastElementId?: string;
  row: DefaultRow;
  tableId: string;
}

export const RemoveButton: React.FC<RemoveButtonProps> = ({
  onRowRemove,
  disableRowRemove,
  hideRowRemove,
  row,
  tableId,
}) => {
  if (hideRowRemove?.(row)) {
    return null;
  }

  return (
    <Button
      data-testid={`${tableId}__removeBtn_${row.index}`}
      buttonStyle="outline"
      color="danger"
      size="sm"
      onClick={() => onRowRemove(row)}
      disabled={disableRowRemove && disableRowRemove(row)}
      tooltipProps={{
        id: `deleteBtn_${row.index}`,
        tooltipContents: 'Delete',
      }}
    >
      <FontAwesomeIcon icon={faTrashAlt} />
    </Button>
  );
};

export const EditButton: React.FC<EditButtonProps> = ({
  getRowEditPath,
  disableRowEdit,
  row,
  tableId,
}) => (
  <Button
    data-testid={`${tableId}__editBtn_${row.index}`}
    color="primary"
    size="sm"
    disabled={disableRowEdit && disableRowEdit(row)}
    to={getRowEditPath(row)}
    tag={Link}
  >
    <FontAwesomeIcon icon={faPencil} /> Edit
  </Button>
);

export const ButtonUp: React.FC<ButtonUpProps> = ({
  onReorder,
  firstElementId,
  row,
  tableId,
}) => {
  const onClick = useCallback(
    () => onReorder({ id: row.original.id, direction: MoveDirection.up }),
    [onReorder, row.original.id]
  );

  return (
    <Button
      data-testid={`${tableId}__upBtn_${row.index}`}
      color="info"
      size="sm"
      onClick={onClick}
      disabled={row.original.id === firstElementId}
      tooltipProps={{
        id: `moveUpBtn_${row.index}`,
        tooltipContents: 'Move up',
      }}
    >
      <FontAwesomeIcon icon={faArrowUp} />
    </Button>
  );
};

export const ButtonDown: React.FC<ButtonDownProps> = ({
  onReorder,
  lastElementId,
  row,
  tableId,
}) => {
  const onClick = useCallback(
    () => onReorder({ id: row.original.id, direction: MoveDirection.down }),
    [onReorder, row.original.id]
  );

  return (
    <Button
      data-testid={`${tableId}__downBtn_${row.index}`}
      color="info"
      size="sm"
      onClick={onClick}
      disabled={row.original.id === lastElementId}
      tooltipProps={{
        id: `moveDownBtn_${row.index}`,
        tooltipContents: 'Move down',
      }}
    >
      <FontAwesomeIcon icon={faArrowDown} />
    </Button>
  );
};

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  onRowDownload,
  row,
  tableId,
}) => (
  <Button
    data-testid={`${tableId}__downloadBtn_${row.index}`}
    buttonStyle="outline"
    color="info"
    size="sm"
    onClick={() => onRowDownload(row)}
    tooltipProps={{
      id: `downloadBtn_${row.index}`,
      tooltipContents: 'Download',
    }}
  >
    <FontAwesomeIcon icon={faDownload} />
  </Button>
);

export const DuplicateButton: React.FC<DuplicateButtonProps> = ({
  onRowDuplicate,
  row,
  tableId,
}) => (
  <Button
    data-testid={`${tableId}__duplicateBtn_${row.index}`}
    buttonStyle="outline"
    color="info"
    size="sm"
    onClick={() => onRowDuplicate(row)}
    tooltipProps={{
      id: `duplicateBtn_${row.index}`,
      tooltipContents: 'Duplicate',
    }}
  >
    <FontAwesomeIcon icon={faCopy} />
  </Button>
);

export const GetLatestButton: React.FC<GetLatestButtonProps> = ({
  onRowGetLatest,
  row,
  tableId,
}) => (
  <Button
    data-testid={`${tableId}__downloadBtn_${row.index}`}
    color="primary"
    size="sm"
    onClick={() => onRowGetLatest(row)}
  >
    <FontAwesomeIcon icon={faSyncAlt} /> Get Latest
  </Button>
);

export const CustomButton: React.FC<CustomButtonProps> = ({
  onCustomButton,
  row,
  tableId,
  text,
  icon,
  iconAfter,
  className,
  tooltip,
}) => (
  <Button
    data-testid={`${tableId}__customdBtn_${row.index}`}
    className={className}
    icon={icon && <FontAwesomeIcon icon={icon} />}
    aria-label={tooltip || text}
    tooltipProps={
      tooltip
        ? {
            id: `${tableId}__customBtnTooltip_${row.index}`,
            tooltipContents: tooltip,
          }
        : undefined
    }
    type="button"
    onClick={() => onCustomButton(row)}
  >
    {iconAfter ? (
      <>
        {text}
        <FontAwesomeIcon icon={iconAfter} />
      </>
    ) : (
      text
    )}
  </Button>
);
